import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { DahliasLogo } from "../components/brand/Logos";
import CmsText from "../components/CmsText";
import { useSiteContentMap } from "../store/useSiteContent";
import { useSiteSettings } from "../hooks/siteSettings";

/**
 * Heritage one-family landing page.
 *
 * Six brand tiles. Each tile is its own full-image artwork that doubles as the
 * link , the whole tile is clickable. External `.nz` siblings open in a new
 * tab, internal Heritage pages stay in the same tab.
 *
 * Below the grid sits a single extra "Also in the world" row for The Etchery
 * & Co (a smaller sibling brand that doesn't yet warrant a full tile).
 */

// CDN-hosted artwork. Replace any URL by re-uploading via the same job.
const TILE_IMG = {
  dahlias:    "https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/kaicq56m_HERITAGE-DAHLIAS-MAIN.png",
  bloomClub:  "https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/mqk0nur7_HERITAGE-BLOOM-CLUB.png",
  bloomKeep:  "https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/330nfuco_THE-BLOOM-KEEPER.png",
  cropasaur:  "https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/x01gj835_CroppedImage_20260629_103139.png",
  apparel:    "https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/wmz7rflz_CroppedImage_20260701_203532.png",
  bloomFoundry: "https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/yknrm2e7_file_000000008510720bba0bdfe6dfce7b7d.png",
};

const TILES = [
  {
    slug: "dahlias",
    label: "Heritage Dahlias",
    href: "/dahlias",
    external: false,
    image: TILE_IMG.dahlias,
    caption: "The heart of Heritage · Est. 1932",
  },
  {
    slug: "bloom-club",
    label: "Heritage Bloom Club",
    href: "https://heritagebloomclub.nz",
    external: true,
    image: TILE_IMG.bloomClub,
    caption: "Join the seasonal flower club",
  },
  {
    slug: "bloom-keeper",
    label: "The Bloom Keeper",
    href: "https://thebloomkeeper.nz",
    external: true,
    image: TILE_IMG.bloomKeep,
    caption: "A gardener's companion",
  },
  {
    slug: "cropasaurus-rex",
    label: "Cropasaurus Rex",
    href: "https://cropasaurusrex.nz",
    external: true,
    image: TILE_IMG.cropasaur,
    caption: "Mass auto-cropping for busy humans",
  },
  {
    slug: "apparel",
    label: "Heritage Apparel",
    href: "https://heritageapparel.nz",
    external: true,
    image: TILE_IMG.apparel,
    caption: "Visit Heritage Apparel",
  },
  {
    slug: "bloom-foundry",
    label: "The Bloom Foundry",
    href: "https://thebloomfoundry.nz",
    external: true,
    image: TILE_IMG.bloomFoundry,
    caption: "OPEN NOW!!!",
    featured: true,
  },
];

function Tile({ t, idx }) {
  const content = (
    <>
      <img
        src={t.image}
        alt={t.label}
        loading={idx < 3 ? "eager" : "lazy"}
        className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
        draggable={false}
      />

      {/* Subtle bottom caption ribbon, appears on hover */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/40 to-transparent p-4 md:p-5 transition-opacity duration-300 ${t.featured ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
      >
        <div className="flex items-center justify-between gap-3 text-[var(--heritage-bone)]">
          <span data-testid={t.featured ? `start-tile-${t.slug}-cta` : undefined} className="font-label tracking-[0.3em] text-[10px] sm:text-[11px] uppercase">
            {t.caption}
          </span>
          {t.external && <ExternalLink className="w-3.5 h-3.5 shrink-0" />}
        </div>
      </div>
    </>
  );

  const className =
    "group relative block aspect-square overflow-hidden rounded-2xl bg-[var(--dahlias-cream)] " +
    "shadow-md hover:shadow-2xl ring-1 ring-[var(--heritage-burgundy)]/15 hover:ring-[var(--heritage-burgundy)]/35 " +
    "transition-all duration-500 hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-[var(--heritage-burgundy)] focus:ring-offset-2 focus:ring-offset-[var(--dahlias-cream)]";

  if (t.external) {
    return (
      <a
        href={t.href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`start-tile-${t.slug}`}
        className={className}
        title={`${t.label} , ${t.caption}`}
      >
        {content}
      </a>
    );
  }
  return (
    <Link
      to={t.href}
      data-testid={`start-tile-${t.slug}`}
      className={className}
      title={`${t.label} , ${t.caption}`}
    >
      {content}
    </Link>
  );
}

const ETCHERY_HREF = "https://theetcheryco.nz";
const ETCHERY_BANNER = "https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/hiwyb4q1_file_000000007310720ba3448a51b742d8e9.png";

export default function StartPage() {
  const { data } = useSiteContentMap();
  const settings = useSiteSettings();
  const announcement = {
    enabled: true,
    text: "THE BLOOM FOUNDRY IS OPEN NOW!!!",
    link_url: "https://thebloomfoundry.nz",
    ...(settings?.announcement || {}),
  };
  const announcementUrl = /^https?:\/\//i.test(announcement.link_url || "")
    ? announcement.link_url
    : "https://thebloomfoundry.nz";
  const tagline =
    (data["start.tagline"] && data["start.tagline"].trim()) ||
    "One family. *Many stories.*";

  return (
    <main
      data-testid="start-page"
      className="relative w-full min-h-[100svh] bg-[var(--dahlias-cream)] text-[var(--heritage-ink)] flex flex-col"
    >
      {/* Sage utility ticker */}
      <div className="bg-[var(--dahlias-sage)] text-[var(--dahlias-cream)]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-2 flex items-center justify-between text-[11px] md:text-xs font-label tracking-[0.35em]">
          <span className="hidden sm:inline">AOTEAROA</span>
          <span>✦ ONE FAMILY MANY STORIES ✦</span>
          <span className="hidden sm:inline">EST. 1932</span>
        </div>
      </div>

      {/* Hero band */}
      <section className="relative bg-[var(--dahlias-sage)] text-[var(--dahlias-cream)] overflow-hidden">
        <div className="relative max-w-3xl mx-auto px-5 md:px-8 pt-12 md:pt-16 pb-16 md:pb-20 text-center">
          <DahliasLogo
            data-testid="start-master-logo"
            variant="seal"
            className="w-[min(50vw,180px)] md:w-[200px] h-auto mx-auto drop-shadow-[0_6px_24px_rgba(20,10,5,0.35)]"
          />
          <h1
            data-testid="start-headline"
            className="mt-6 font-display uppercase tracking-[0.14em] text-3xl sm:text-4xl md:text-5xl text-[var(--dahlias-cream)] leading-[1.05] whitespace-nowrap"
          >
            HERITAGE
          </h1>
          <CmsText
            as="p"
            data-testid="start-tagline"
            text={tagline}
            italicClass="font-display-italic text-[var(--dahlias-cream)]"
            className="mt-5 max-w-md mx-auto text-base md:text-xl text-[var(--dahlias-cream)]/95 leading-relaxed"
          />
          <div className="mt-6 flex items-center justify-center gap-4 text-[var(--dahlias-cream)]/80">
            <span aria-hidden="true" className="h-px w-12 md:w-20 bg-current opacity-60" />
            <span className="font-label tracking-[0.4em] text-[10px] sm:text-xs uppercase">
              Choose a doorway
            </span>
            <span aria-hidden="true" className="h-px w-12 md:w-20 bg-current opacity-60" />
          </div>
        </div>
      </section>

      {announcement.enabled !== false && announcement.text?.trim() && (
        <a href={announcementUrl} target="_blank" rel="noopener noreferrer" data-testid="start-rolling-announcement"
          aria-label={`${announcement.text.trim()} - opens in a new tab`}
          className="group block h-9 overflow-hidden border-y bg-[var(--dahlias-cream)] text-[var(--heritage-burgundy)] hover:bg-white transition-colors"
          style={{ borderColor: "rgba(110,30,58,0.24)" }}>
          <span className="heritage-ticker-track flex h-full w-max items-center whitespace-nowrap font-label text-[10px] sm:text-xs tracking-[0.32em] uppercase font-semibold">
            {[0, 1, 2, 3].map((part) => (
              <span key={part} aria-hidden={part > 0} className="inline-flex items-center gap-5 px-5">
                <span aria-hidden="true">✦</span>
                <span>{announcement.text.trim()}</span>
                <ExternalLink className="w-3 h-3" />
              </span>
            ))}
          </span>
        </a>
      )}

      {/* Six-tile grid */}
      <section
        data-testid="start-businesses"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7 px-5 md:px-10 pt-10 md:pt-14 pb-6 md:pb-10 max-w-7xl mx-auto w-full"
      >
        {TILES.map((t, i) => (
          <Tile key={t.slug} t={t} idx={i} />
        ))}
      </section>

      {/* Extra siblings row , The Etchery & Co partner banner */}
      <section className="max-w-5xl mx-auto w-full px-5 md:px-10 pb-12 md:pb-16">
        <div className="flex items-center justify-center gap-4 mb-5 text-[var(--dahlias-sage-deep)]">
          <span aria-hidden="true" className="h-px w-12 md:w-20 bg-current opacity-40" />
          <span
            data-testid="start-partners-heading"
            className="tracking-[0.5em] text-[11px] sm:text-xs uppercase opacity-90"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600 }}
          >
            OUR PARTNERS
          </span>
          <span aria-hidden="true" className="h-px w-12 md:w-20 bg-current opacity-40" />
        </div>
        <a
          href={ETCHERY_HREF}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="start-tile-etchery"
          className="group block max-w-3xl mx-auto overflow-hidden rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-0.5"
          title="The Etchery & Co , our new partner in crime"
          style={{ aspectRatio: "5 / 1" }}
        >
          <img
            src={ETCHERY_BANNER}
            alt="The Etchery & Co , our new partner in crime"
            loading="lazy"
            className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.02]"
            draggable={false}
          />
        </a>
      </section>

      <footer className="bg-[var(--dahlias-sage)] text-[var(--dahlias-cream)] px-5 py-10 text-xs md:text-sm mt-auto">
        <div className="max-w-4xl mx-auto flex flex-col items-center gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <a
              href="/admin/login"
              data-testid="footer-admin-login"
              className="group inline-flex items-center gap-3 px-5 py-2.5 rounded-full border border-[var(--dahlias-cream)]/30 hover:border-[var(--dahlias-cream)]/70 hover:bg-[var(--dahlias-cream)]/5 transition"
              title="Heritage Dahlias admin login"
            >
              <img
                src="/photos/brand/heritage-seal.png"
                alt=""
                className="w-7 h-7 opacity-90 group-hover:opacity-100 transition"
                style={{ filter: "brightness(0) invert(1)" }}
              />
              <span className="font-serif tracking-[0.35em] uppercase text-[11px] md:text-xs">
                Admin Login
              </span>
            </a>
            <a
              href="/admin/login?next=/console"
              data-testid="footer-management-login"
              className="group inline-flex items-center gap-3 px-5 py-2.5 rounded-full border border-[var(--dahlias-cream)]/30 hover:border-[var(--dahlias-cream)]/70 hover:bg-[var(--dahlias-cream)]/5 transition"
              title="Heritage Dahlias management portal login"
            >
              <img
                src="/photos/brand/heritage-seal.png"
                alt=""
                className="w-7 h-7 opacity-90 group-hover:opacity-100 transition"
                style={{ filter: "brightness(0) invert(1)" }}
              />
              <span className="font-serif tracking-[0.35em] uppercase text-[11px] md:text-xs">
                Management Login
              </span>
            </a>
          </div>
          <div className="text-center opacity-80">© {new Date().getFullYear()} Heritage · For Nana Jean, born 1932</div>
        </div>
      </footer>
    </main>
  );
}
